import {
  cameraTypeList,
  keyControlIncludedEquipmentCameraSelectionVulcan,
  keyControlAdditionalEquipmentVulcan,
  keyMonitorAdditionalEquipmentVulcan,
  keyProtectAdditionalEquipmentVulcan,
  keyProtectIncludedEquipmentCameraSelectionVulcan,
  secureBusinessMaxCameraLimit,
  secureBusinessOppositeSectionKeysVulcan,
  smartCameraAdditionalEquipmentVulcan,
  smartCameraIncludedEquipmentCameraSelectionVulcan,
  typeSwitch
} from '../../../../utils/constant';

/*
 * Checks if adding a product would exceed section or combined limits.
 * Enforces: (1) combined camera limit across opposite sections, (2) section max value for
 * included sections, (3) general section max when not camera-only, (4) temperature probe
 * (Probe) limit of 10 across additional equipment sections.
 * Calls onOverLimit(message, limitValue) when a limit is exceeded; returns
 * { totalOverLimit, isCameraLimit } for the caller to block or allow the change.
 */
export const checkIfTotalOverLimit = (opt, fieldProductType, context) => {
  const {
    formFields,
    sectionName,
    formObjectKeys,
    maxValue,
    isLimitCameraOnly,
    maxCameraLimitErrorMessage,
    errorMessage,
    onOverLimit
  } = context;
  const returnObj = { totalOverLimit: false, isCameraLimit: false };

  const sumSectionByPredicate = (sectionObj, keys, productTypePredicate) =>
    keys
      .filter(k => productTypePredicate((sectionObj[k] || {}).productType))
      .reduce((acc, k) => acc + parseInt((sectionObj[k] || {}).value || 0, 10), 0);

  const reportOverLimit = (overLimit, isCameraLimit, message, limitValue) => {
    returnObj.totalOverLimit = overLimit;
    returnObj.isCameraLimit = isCameraLimit;
    if (overLimit && onOverLimit) {
      onOverLimit(message, limitValue);
    }
    return returnObj;
  };

  const cameraSectionKeys = [
    keyProtectIncludedEquipmentCameraSelectionVulcan,
    keyProtectAdditionalEquipmentVulcan,
    keyControlIncludedEquipmentCameraSelectionVulcan,
    keyControlAdditionalEquipmentVulcan,
    keyMonitorAdditionalEquipmentVulcan,
    smartCameraIncludedEquipmentCameraSelectionVulcan,
    smartCameraAdditionalEquipmentVulcan
  ];
  const includedSectionKeys = [
    keyProtectIncludedEquipmentCameraSelectionVulcan,
    keyControlIncludedEquipmentCameraSelectionVulcan,
    smartCameraIncludedEquipmentCameraSelectionVulcan
  ];
  const additionalSectionKeys = [
    keyProtectAdditionalEquipmentVulcan,
    keyControlAdditionalEquipmentVulcan,
    keyMonitorAdditionalEquipmentVulcan
  ];

  const isCameraSection = cameraSectionKeys.includes(sectionName);
  const isCameraProduct = cameraTypeList.includes(fieldProductType);
  const isIncludedSection = includedSectionKeys.includes(sectionName);

  if (isCameraSection && isCameraProduct) {
    const otherSectionKey = secureBusinessOppositeSectionKeysVulcan[sectionName];
    const otherSection = formFields[otherSectionKey] || {};
    const otherKeys = Object.keys(otherSection);
    const otherSectionSum = sumSectionByPredicate(
      otherSection,
      otherKeys,
      productType => cameraTypeList.includes(productType)
    );
    const currentSum = sumSectionByPredicate(
      opt,
      formObjectKeys,
      productType => cameraTypeList.includes(productType)
    );
    if (otherSectionSum + currentSum > secureBusinessMaxCameraLimit) {
      return reportOverLimit(
        true,
        true,
        maxCameraLimitErrorMessage,
        secureBusinessMaxCameraLimit
      );
    }
    if (isIncludedSection && typeof maxValue === 'number') {
      const sectionSum = sumSectionByPredicate(
        opt,
        formObjectKeys,
        productType => productType !== typeSwitch
      );
      if (sectionSum > maxValue) {
        return reportOverLimit(true, false, errorMessage, maxValue);
      }
    }
  } else if (typeof maxValue === 'number' && !isLimitCameraOnly) {
    const currentSum = sumSectionByPredicate(
      opt,
      formObjectKeys,
      productType => productType !== typeSwitch
    );
    if (currentSum > maxValue) {
      return reportOverLimit(true, false, errorMessage, maxValue);
    }
  }

  if (additionalSectionKeys.includes(sectionName) && fieldProductType === 'Probe') {
    const otherSectionKey = secureBusinessOppositeSectionKeysVulcan[sectionName];
    const otherSection = formFields[otherSectionKey] || {};
    const otherKeys = Object.keys(otherSection);
    const otherSectionSum = sumSectionByPredicate(
      otherSection,
      otherKeys,
      productType => productType === 'Probe'
    );
    const currentSum = sumSectionByPredicate(
      opt,
      formObjectKeys,
      productType => productType === 'Probe'
    );
    if (otherSectionSum + currentSum > 10) {
      return reportOverLimit(
        true,
        true,
        'A maximum of %s temperature sensors can be selected in total',
        10
      );
    }
  }

  return returnObj;
};
